/**
 * The task timer and the Pomodoro together (Settings → Pomodoro): *auto-pause during breaks* and *link manual
 * pauses*. The decisions are pure (`autoPauseStep`); the writers below apply them and the links.
 */
import type { LoroDoc } from "loro-crdt";
import { readSettings } from "../app/settings";
import {
  currentState,
  pausePomodoro,
  readPomodoro,
  resumePomodoro,
  type PomodoroSettings,
  type PomodoroState,
} from "./pomodoro";
import { pauseTracking, readCurrent, resumeTracking, type TrackingCurrent } from "./timelog";

export type AutoStep = { kind: "pause" | "resume"; at: number } | null;

/**
 * What auto-pause does now: in a break, a running timer pauses as of the break's start (never before its own
 * start); back at work — or with the Pomodoro stopped — a timer that a break paused resumes. A timer paused by hand
 * is never resumed, and a paused Pomodoro changes nothing.
 */
export function autoPauseStep(
  cur: TrackingCurrent | null,
  pom: PomodoroState | null,
  s: PomodoroSettings & { autoPauseInBreaks?: boolean },
  nowMs: number,
): AutoStep {
  if (!s.autoPauseInBreaks || !cur) return null;
  const autoPaused = !!cur.pausedAt && cur.pausedBy === "pomodoro";
  if (!pom) return autoPaused ? { kind: "resume", at: nowMs } : null;
  if (pom.pausedAt) return null;
  const state = currentState(pom, s, nowMs);
  const since = Date.parse(state.phaseStartedAt);
  if (state.phase !== "work")
    return cur.pausedAt
      ? null
      : { kind: "pause", at: Math.min(nowMs, Math.max(since, Date.parse(cur.startedAt))) };
  return autoPaused
    ? { kind: "resume", at: Math.min(nowMs, Math.max(since, Date.parse(cur.pausedAt!))) }
    : null;
}

let busy = false;

/** Apply auto-pause once (called every second by the shell and the big view); safe to call repeatedly. */
export async function applyAutoPause(core: LoroDoc, workspaceId: string, nowMs: number): Promise<void> {
  if (busy) return;
  const step = autoPauseStep(readCurrent(core), readPomodoro(core), readSettings(core).pomodoro, nowMs);
  if (!step) return;
  busy = true;
  try {
    if (step.kind === "pause") await pauseTracking(core, new Date(step.at), "pomodoro");
    else await resumeTracking(core, workspaceId, new Date(step.at));
  } finally {
    busy = false;
  }
}

const linked = (core: LoroDoc) => readSettings(core).pomodoro.linkPauses === true;

/** Pause the task timer by hand; with linked pauses the Pomodoro pauses too. */
export async function pauseTimer(core: LoroDoc): Promise<void> {
  await pauseTracking(core);
  const p = readPomodoro(core);
  if (linked(core) && p && !p.pausedAt) pausePomodoro(core);
}

export async function resumeTimer(core: LoroDoc, workspaceId: string): Promise<void> {
  await resumeTracking(core, workspaceId);
  const p = readPomodoro(core);
  if (linked(core) && p?.pausedAt) resumePomodoro(core);
}

/** Pause the Pomodoro by hand; with linked pauses the task timer pauses too. */
export async function pausePom(core: LoroDoc): Promise<void> {
  pausePomodoro(core);
  const cur = readCurrent(core);
  if (linked(core) && cur && !cur.pausedAt) await pauseTracking(core);
}

export async function resumePom(core: LoroDoc, workspaceId: string): Promise<void> {
  resumePomodoro(core);
  const cur = readCurrent(core);
  if (linked(core) && cur?.pausedAt) await resumeTracking(core, workspaceId);
}
